import { Badge } from '@saay/shadcn/components/badge';
import { cn } from '@saay/shadcn/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The brand tones (D-80 palette, theme tokens `--tone-*`): olive = money in, purchases, active;
 * gold = history, referral, notices; clay = installments, due today, waiting; copper = attempts,
 * near a limit; forest = card, neutral, upcoming; danger = overdue and errors only.
 */
export type Tone = 'olive' | 'gold' | 'clay' | 'copper' | 'forest' | 'danger';

/** Tint background with its text colour. */
export const TONE_SOFT: Record<Tone, string> = {
  olive: 'bg-tone-olive text-tone-olive-foreground',
  gold: 'bg-tone-gold text-tone-gold-foreground',
  clay: 'bg-tone-clay text-tone-clay-foreground',
  copper: 'bg-tone-copper text-tone-copper-foreground',
  forest: 'bg-tone-forest text-tone-forest-foreground',
  danger: 'bg-tone-danger text-tone-danger-foreground',
};

/** The strong colour, for dots and indicator lines. */
export const TONE_STRONG: Record<Tone, string> = {
  olive: 'bg-tone-olive-strong',
  gold: 'bg-tone-gold-strong',
  clay: 'bg-tone-clay-strong',
  copper: 'bg-tone-copper-strong',
  forest: 'bg-tone-forest-strong',
  danger: 'bg-tone-danger-strong',
};

const TILE = {
  sm: ['size-9 rounded-xl', 'size-4'],
  md: ['size-11 rounded-2xl', 'size-5'],
  lg: ['size-14 rounded-2xl', 'size-6'],
} as const;

/** A tinted square holding one icon: the only place colour enters a block. */
export function IconTile({
  icon: Icon,
  tone,
  size = 'md',
  className,
}: {
  readonly icon: LucideIcon;
  readonly tone: Tone;
  readonly size?: keyof typeof TILE;
  readonly className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn('grid shrink-0 place-items-center', TILE[size][0], TONE_SOFT[tone], className)}
    >
      {/* The size sits on the icon itself so a parent Button's icon rule does not shrink it. */}
      <Icon className={TILE[size][1]} />
    </span>
  );
}

/** A status as a soft badge with a dot; the text comes from the central status dictionary. */
export function StatusBadge({
  tone,
  children,
  className,
}: {
  readonly tone: Tone;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <Badge className={cn('gap-1.5', TONE_SOFT[tone], className)}>
      <span className={cn('size-1.5 rounded-full', TONE_STRONG[tone])} />
      {children}
    </Badge>
  );
}
